/*========================================================================
 * $Id$
 *
 * Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2013
 *
 * Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
 * Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
 * Urheber festgelegten Stelle in der Funktion des Computerprogramms
 * angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
 * Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
 * Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
 * =========================================================================*/

define([
  "client/template",
  "component!base",
  "i18n/i18n",
  "underscore",
  "utils/clone",
  "utils/lzma-url"
], function(
  Template,
  ComponentBase,
  i18n,
  _,
  deepClone,
  Lzma
) {
  
  var ChatTemplate = Template.extend({
    editableName: "editor_ai_chat",

    initialize: function() {
      this._addedNodes = [];
      Template.prototype.initialize.apply(this, _.toArray(arguments));
    },

    schema: new Template.Schema({
      attributes: {
        "pipeline-selection": {
          type: "string",
          title: "editor_pipeline_selection",
          values: [
            { value: "key", text: i18n('editor_pipeline_selection_by_key')},
            { value: "id", text: i18n('editor_pipeline_selection_by_id')}
          ],
          default: 'key',
        },
        "pipeline-selection-value": {
          type: "string",
          title: "editor_pipeline_selection_value",
        },
        "use-search-input": {
          type: "boolean",
          title: "editor_answers_use_user_search",
          default: true
        },
        "use-user-constraints": {
          type: "boolean",
          title: "editor_answers_use_user_constraints",
          default: true
        },
        "use-source-context-constraints": {
          type: "boolean",
          title: "editor_answer_use_source_context_constraints",
          default: true
        },
        "restrict-to-sources": {
          title: "editor_restrict_to_sources",
          type: "boolean",
          default: false,
          visible: true
        },
        "enable-citation" : {
          title: "editor_answer_enable_citation",
          type: "boolean",
          default: true
        },
        "use-metadatakeys" : {
          title: "editor_answer_use_metadatakeys",
          type: "boolean",
          default: false
        },
        "metadatakeys": {
          title: "editor_answer_metadatakeys",
          type: "list",
          depends: "/^true$/i.test(attributes['useMetadatakeys']) ",
        },
        "initialize-from-user-query": {
          title: "editor_chat_initialize_from_user_query",
          type: "boolean",
          default: false
        },
        "initial-user-input": {
          title: "editor_chat_initial_user_input",
          type: "string"
        },
        "show-create-new-conversation": {
          title: "editor_chat_show_create_new_conversation",
          type: "boolean",
          default: true
        },
        height: {
          type: "string",
          title: "editor_chat_height",
          default: "auto",
          visible: false,
          values: [
            { value: "auto", text: i18n("editor_chat_automatic_view") },
            { value: "95vh", text: i18n("editor_chat_full_view") },
            { value: "75vh", text: i18n("editor_chat_third_view") },
            { value: "50vh", text: i18n("editor_chat_half_view") },
            { value: "25vh", text: i18n("editor_chat_quarter_view") }
          ]
        },
        "modelid": {
          type: "string",
          title: "editor_modelid",
          visible: false
        },
        "apply-answers-settings": {
          title: "editor_chat_apply_answers_settings",
          type: "boolean",
          default: true
        },
        "initial-assistant-message-i18n": {
          title: "editor_initial_assistant_message_i18n",
          type: "json",
          visible: false
        },
        "assistant-module": {
          type: "string",
          title: "editor_assistant_module",
          visible: false
        },
        "enable-voting": {
          type: "boolean",
          default: true,
          visible: false
        },
        "show-ai-disclaimer": {
          title: "editor_show_ai_disclaimer_text",
          type: "boolean",
          default: true,
          visible: true
        },
        "ai-disclaimer-text": {
          type: "string",
          title: "editor_ai_disclaimer_text",
          visible: true,
          depends: "!/^false$/i.test(attributes['showAiDisclaimer'])"
        },
      }
    }),

    hasContent: function() {
      return true;
    },

    getContentSchema: function() {
      return {
        type: "template"
      };
    },

    getOptions: function(options) {
      this.useCamelCaseNames = true;
      // @todo the merge process in initActions need the properties here
      // @workaround this.get() returns the attribute model as an object
      // options should be string so the parseAttributes will chnage them to object if needed
      var attrs = this.get();

      var attrs = this.get();
      var newAtts = {};
      if (attrs.showAiDisclaimer) {
        newAtts.showAiDisclaimer = attrs.showAiDisclaimer;
      }
      if (attrs.aiDisclaimerText) {
        newAtts.aiDisclaimerText = attrs.aiDisclaimerText;
      }

      // @todo implement a more rubost solution to merge attributes
      options = _.extend(
        {},
        attrs,
        Template.prototype.getOptions.apply(this, arguments),
        this.attributeModel.getCamelCase(),
        newAtts
      );
      return this.schema.parseAttributes(options);
    },

    // At this point, the default values are as kebap case, and changed values are accesable only as cammelCase
    getDefaultOrChangedValue: function(options, optionCamelCaseName, optionKebapCaseName) {
      return options[optionCamelCaseName] !== undefined ? options[optionCamelCaseName] : options[optionKebapCaseName];
    },

    getValueAsBoolean: function(value) {
      if (typeof value === 'string') {
        return value === 'true';
      }
      return !!value;
    },

    createInsightChatEl: function() {
      const chatEl = document.createElement("insight-chat");

      const options = this.getOptions();
      if (options.modelid) {
        chatEl.setAttribute('modelid', options.modelid);
      } else if (this.getDefaultOrChangedValue(options, 'pipelineSelectionValue', 'pipeline-selection-value')) {
        chatEl.setAttribute('pipeline-selection', this.getDefaultOrChangedValue(options, 'pipelineSelection', 'pipeline-selection'));
        chatEl.setAttribute('pipeline-selection-value', this.getDefaultOrChangedValue(options, 'pipelineSelectionValue', 'pipeline-selection-value'));
      }
      chatEl.setAttribute('enable-citation', this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'enableCitation', 'enable-citation')));
      chatEl.setAttribute('use-search-input', this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'useSearchInput', 'use-search-input')));
      chatEl.setAttribute('use-user-constraints', this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'useUserConstraints', 'use-user-constraints')));
      chatEl.setAttribute('use-source-context-constraints', this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'useSourceContextConstraints', 'use-source-context-constraints')));
      chatEl.setAttribute('show-ai-disclaimer', this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'showAiDisclaimer', 'show-ai-disclaimer')));
      chatEl.setAttribute('ai-disclaimer-text', this.getDefaultOrChangedValue(options, 'aiDisclaimerText', 'ai-disclaimer-text') || '');

      if (this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'initializeFromUserQuery', 'initialize-from-user-query'))) {
        chatEl.setAttribute('initialize-from-user-query', true);
      }      
      
      if (this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'enableVoting', 'enable-voting'))) {
        chatEl.setAttribute('enable-voting', true);
      }      
      
      if (this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'restrictToSources', 'restrict-to-sources'))) {
        chatEl.setAttribute('restrict-to-sources', true);
      }
      
      if (!this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'showCreateNewConversation', 'show-create-new-conversation'))) {
        chatEl.setAttribute('show-create-new-conversation', false);
      }

      if (!this.getValueAsBoolean(this.getDefaultOrChangedValue(options, 'applyAnswersSettings', 'apply-answers-settings'))) {
        chatEl.setAttribute('apply-answers-settings', false);
      }

      if (!!this.getDefaultOrChangedValue(options, 'useMetadatakeys', 'use-metadatakeys') && options.metadatakeys) {
        chatEl.setAttribute('metadatakeys', options.metadatakeys);
      }
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!this.getDefaultOrChangedValue(options, 'initialUserInput', 'initial-user-input')) {
        chatEl.setAttribute('initial-user-input', this.getDefaultOrChangedValue(options, 'initialUserInput', 'initial-user-input'));
      }
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!this.getDefaultOrChangedValue(options, 'height', 'height')) {
        chatEl.setAttribute('height', this.getDefaultOrChangedValue(options, 'height', 'height'));
      }
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!this.getDefaultOrChangedValue(options, 'initialAssistantMessageI18n', 'initial-assistant-message-i18n')) {
        chatEl.setAttribute('initial-assistant-message-i18n', this.getDefaultOrChangedValue(options, 'initialAssistantMessageI18n', 'initial-assistant-message-i18n'));
      }

      // eslint-disable-next-line no-extra-boolean-cast
      if (!!this.getDefaultOrChangedValue(options, 'assistantModule', 'assistant-module')) {
        chatEl.setAttribute('assistant-module',  this.getDefaultOrChangedValue(options, 'assistantModule', 'assistant-module'));
      }

      return chatEl;
    },

    createDefaultTemplateIfNonePresent: function() {
      const isTemplatePresent = this.getTemplateNodeHasChildren()
      if (!isTemplatePresent) {
        // TODO in designer do not copy!
        const el = document.createElement("div");
        el.className = "chat";
        var chatEl = this.createInsightChatEl();
        el.appendChild(chatEl);
        
        this._addedNodes.push(el);
        return Promise.resolve(this.append(el));
      }
      else{
        // TODO: migrgate existing templates
        return Promise.resolve(this.node && this.node.children || []);
      }
    },

    remove: function (options) {
      // This is called on destroy
      _.forEach(this.instances, function (view) {
        view.remove();
      });
      this.instances = [];
      var nodeChildren = options && options.node && options.node.children;
      _.forEach(nodeChildren, function (childNode) {
        childNode && childNode.template && childNode.template.remove({
          node: childNode
        });
      });
      for (const node of this._addedNodes) {
        try {
          node.parentNode.removeChild(node);
        } catch (e) {}
      }
      this._addedNodes = [];
    }
  });

  ChatTemplate.View = ComponentBase.extend({
    initialize: function() {
      ComponentBase.prototype.initialize.apply(this, arguments);
    },

    defaultChannel: function() {
      //@todo check if there is any other way !
      // defaultChannel is not working if the sources set via application options
      // return this.options.application.models.defaultChannels.at(0).get('url') + "../..";
      return this.options.application.channels.at(0).get("url") + "../..";
    },

    getRetrivalOptions: function() {
      var asUrlParam = false;
      var constraint = this.constraint(asUrlParam);
      var searchRequest = this.getSearchRequest();
      if (searchRequest && constraint) {
        return { constraint: constraint, search_request: searchRequest };
      } else if (!searchRequest && constraint) {
        return { constraint: constraint };
      } else if (searchRequest && !constraint) {
        return { search_request: searchRequest };
      }
      return undefined;
    },


    getRetrivalOptionsAsUrlParam: function() {
      var retrievalOptions = this.getRetrivalOptions();
      if (!retrievalOptions) {
        return null;
      }
      return Lzma.compressUrlSafe(JSON.stringify(retrievalOptions));
    },

    constraint: function(asUrlParam) {
      var constraint = this.options.constraint;
      if (constraint && isNaN(constraint)) {
        if (asUrlParam) {
          if (constraint.unparsed) {
            asUrlParam.searchParams.set("retrieval_constraint_unparsed", constraint.unparsed);
            return asUrlParam;
          } else {
            asUrlParam.searchParams.set("retrieval_constraint", Lzma.compressUrlSafe(JSON.stringify(constraint)));
            return asUrlParam;
          }
        }
        return constraint;
      }
      return undefined;
    },

    getSearchRequest: function() {
      var searchRequest = this.model.input.toJSON();
      searchRequest = deepClone(searchRequest);
      if (searchRequest) {
        var options = this.options;
        var user = searchRequest.user;
        var source_context = searchRequest.source_context;

        function deletePropertyIfConditionMet(keepProperty, object, property) {
          if ((keepProperty === "false" || keepProperty === false) && object && object[property]) {
            delete object[property];
          }
        }

        deletePropertyIfConditionMet(options.useUserQuery, user, "query");
        deletePropertyIfConditionMet(options.useUserConstraints, user, "constraints");
        deletePropertyIfConditionMet(options.useSourceContextConstraints, source_context, "constraints");
        deletePropertyIfConditionMet(options.useSourceContextChatSourcesConstraint, source_context.constraints, "chat_sources");
      }
      return searchRequest;
    },

    setParamLanguage: function(url) {
      url.searchParams.set("language", (i18n && i18n.currentLocale()) || "en");
      return url;
    },

    setParamShowModelSetting: function(url) {
      var showModel = this.options.showModel;
      url.searchParams.set("show_model", showModel);
      return url;
    },

    setParamShowSideNavigationSetting: function(url) {
      var showSideNavigation = this.options.showSideNavigation;
      url.searchParams.set("show_side_navigation", showSideNavigation);
      return url;
    },

    getIframeSrc: function() {
      var defaultChannel = this.defaultChannel();
      return defaultChannel + "/apps/chat";
    }
  });

  return ChatTemplate;
});
